import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import {
  AttachmentsAPI,
  FileAttachment,
  UpdateAttachmentComment,
} from 'api/attachments';
import { AttachmentType } from 'api/attachments/types';
import { Discussion } from 'api/document/types/document';

const ATTACHMENT = 'attachment';
const ATTACHMENT_VERSION = 'attachmentVersion';
const ATTACHMENT_DISCUSSION = 'attachment-discussions';
const ADD_COMMENT_ON_ATTACHMENT = 'add-comment-on-attachment';
const UPDATE_COMMENT_ON_ATTACHMENT = 'update-comment-on-attachment';

export const useAttachStatusUpdateFileMutation = (
  statusUpdateId: number,
  projectId: number,
): UseMutationResult<FileAttachment, unknown, { file: File }> => {
  return useMutation<FileAttachment, unknown, { file: File }>(({ file }) =>
    AttachmentsAPI.attachStatusUpdateFiles(file, {
      statusUpdateId,
      projectId,
    }),
  );
};

export const useAttachResourceRequestFileMutation = (
  projectId: number,
): UseMutationResult<FileAttachment, unknown, { file: File }> => {
  return useMutation<FileAttachment, unknown, { file: File }>(({ file }) =>
    AttachmentsAPI.attachFiles(file, {
      projectId,
    }),
  );
};

export const useAttachmentById = (
  attachmentId: number,
): UseQueryResult<FileAttachment, unknown> => {
  return useQuery(
    [ATTACHMENT, Number(attachmentId)],
    () => AttachmentsAPI.getAttachment(attachmentId),
    { enabled: !!attachmentId },
  );
};

export const useGetDiscussionsForAttachment = (
  attachmentId: number,
): UseQueryResult<
  {
    discussions: Discussion[];
  },
  unknown
> =>
  useQuery(
    [ATTACHMENT_DISCUSSION, Number(attachmentId)],
    () => AttachmentsAPI.getDiscussions(attachmentId),
    {
      enabled: !!attachmentId,
      notifyOnChangeProps: ['data', 'dataUpdatedAt', 'isRefetching'],
    },
  );

export const useAddCommentOnAttachment = (attachmentId: number) => {
  const { refetch } = useGetDiscussionsForAttachment(attachmentId);

  return useMutation(
    [ADD_COMMENT_ON_ATTACHMENT, attachmentId],
    (body: UpdateAttachmentComment) =>
      AttachmentsAPI.addComment(attachmentId, body).then(() => {
        refetch();
      }),
  );
};

export const useUpdateCommentOnAttachment = (
  attachmentId: number,
  commentId: number,
) => {
  const { refetch } = useGetDiscussionsForAttachment(attachmentId);

  return useMutation(
    [UPDATE_COMMENT_ON_ATTACHMENT, attachmentId, commentId],
    (body: UpdateAttachmentComment) =>
      AttachmentsAPI.updateComment(attachmentId, commentId, body).then(() => {
        refetch();
      }),
  );
};

export const useAttachmentVersionById = (
  attachmentVersionId: number,
): UseQueryResult<FileAttachment, unknown> => {
  return useQuery(
    [ATTACHMENT_VERSION, Number(attachmentVersionId)],
    () => AttachmentsAPI.getAttachmentVersion(attachmentVersionId),
    { enabled: !!attachmentVersionId },
  );
};

export const useAllAttachmentVersionById = (
  attachmentId?: number,
): UseQueryResult<FileAttachment[], unknown> => {
  return useQuery([ATTACHMENT_VERSION, attachmentId], () =>
    AttachmentsAPI.getAllAttachmentVersions(attachmentId),
  );
};

export const useAttachmentVersionMutation = (
  parentAttachmentId: number,
  data: { projectId: number; sourceId?: number; sourceType?: AttachmentType },
): UseMutationResult<FileAttachment, unknown, { file: File }> => {
  const queryClient = useQueryClient();

  return useMutation<FileAttachment, unknown, { file: File }>(
    ({ file }) =>
      AttachmentsAPI.createAttachmentVersion(parentAttachmentId, file, {
        projectId: data.projectId,
        sourceId: data.sourceId,
        sourceType: data.sourceType,
      }),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(ATTACHMENT_VERSION);
      },
    },
  );
};

export const useAttachmentVersionDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (attachmentId: number) =>
      AttachmentsAPI.deleteAttachmentVersion(attachmentId),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(ATTACHMENT_VERSION);
      },
    },
  );
};

export const useMarkAsCurrentVersion = (): UseMutationResult<
  void,
  unknown,
  any
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, any>(
    ({ attachmentId, versionMappingId }) =>
      AttachmentsAPI.markAsCurrentVersion(attachmentId, versionMappingId),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(ATTACHMENT_VERSION);
      },
    },
  );
};
